import { useEffect, useState } from 'react';
import './NewPortalPage.css';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import { CharacterLimitIndicator } from '../helpers/CharacterLimitIndicator';
import { useTheme } from '../../store/ThemeContext';
import { fetchPopulationGroups } from '../../services/populationGroupService';
import { useAuthContext } from '../../store/Contexts';
import { useAdminAudienceEnabled } from '../../hooks/useAdminAudienceEnabled';
import Tooltip from '../ui/tooltips/Tooltip';
import { Dropdown } from '../helpers/Dropdown';
import AudienceBadge from '../shared/AudienceBadge';
import { getAgencies } from '../../services/systemService';
import { getDateFromZulu } from '../helpers/getDateFromZulu';
import { TenantTiers } from '../../utils/data/enums';
import Toast from '../ui/toasts/Toast';
import { getText } from '../../i18n';

const AttributesTab = ({
    savedPage,
    setSavedPage,
    savePage,
    updatePopulationGroups,
    loadFromMultiTenantTemplates = false,
    loadFromClientTemplates = false,
    isActivePage = false,
    hasDraft = false,
}) => {
    const [populationGroups, setPopulationGroups] = useState([]);
    const { companyConfig } = useAuthContext();
    const [templateScope, setTemplateScope] = useState('Select Scope');
    const { user, clientTierMeetsRequirement } = useAuthContext();
    const { isAdminAudienceEnabled } = useAdminAudienceEnabled();
    const [userDefaultGroup, setUserDefaultGroup] = useState(null);
    const [agencies, setAgencies] = useState([]);

    const fetchAgencies = async () => {
        try {
            const fetchedAgencies = await getAgencies();
            setAgencies(fetchedAgencies.viewModels || []);
        } catch (error) {
            console.error('Failed to fetch agencies', error);
            Toast.error(getText('msg_27ded7882fa5'));
        }
    };

    useEffect(() => {
        fetchAgencies();
    }, []);

    useEffect(() => {
        if (agencies.length > 0 && savedPage?.id) {
            const matchedAgency = agencies.find(
                a => a.id === savedPage.agencyId,
            );
            if (matchedAgency) {
                setTemplateScope(matchedAgency.name);
                setSavedPage(prevState => ({
                    ...prevState,
                    scope: matchedAgency.name,
                    agencyId: matchedAgency.id,
                }));
            }
        }
    }, [agencies]);

    const saveCurrentFields = () => {
        savePage(2); // Call savePage prop and pass 2 as triggerAction, aka "Next" button
    };

    const handleNameChange = event => {
        setSavedPage(prevState => ({
            ...prevState,
            name: event.target.value,
        }));

        handleNameCharacterLimit(event);
    };

    const handleDescriptionChange = event => {
        setSavedPage(prevState => ({
            ...prevState,
            description: event.target.value,
        }));

        handleDescriptionCharacterLimit(event);
    };

    const handleDisplayNameChange = event => {
        setSavedPage(prevState => ({
            ...prevState,
            displayName: event.target.value,
        }));
    };

    useEffect(() => {
        fetchPopulationGroups().then(response => {
            const defaultGroupId = isAdminAudienceEnabled
                ? user?.jwt?.df
                : null;
            const defaultGroup = response.find(
                group => group.id === defaultGroupId,
            );
            setUserDefaultGroup(defaultGroup || null);
            const filtered = defaultGroupId
                ? response.filter(group => group.id !== defaultGroupId)
                : response;
            setPopulationGroups(filtered);
        });
    }, [isAdminAudienceEnabled, user?.jwt?.df]);

    const handleNameCharacterLimit = event => {
        var str = event.target.value.toString();
    };

    const handleDescriptionCharacterLimit = event => {
        var str = event.target.value.toString();
    };

    const handleIsMainPortal = event => {
        setSavedPage(prevState => ({
            ...prevState,
            isChild: !event.target.checked, //If checkbox is checked, we assign "false" value to "isChild" property to accommodate migration from "Set as Child" to setting as "Main Portal"
        }));
    };

    const handleIsAdditionalUserVisibleChange = event => {
        setSavedPage(prevState => ({
            ...prevState,
            isAdditionalUserVisible: event.target.checked,
        }));
    };

    const themeStyles = useTheme();

    const userAgencies = user?.jwt?.agencies || [];
    const filteredAgencies = agencies.filter(
        agency => agency.id !== 0 || userAgencies.includes(0),
    );

    const scopeOptions = filteredAgencies.map(agency => ({
        value: agency.name,
        label: agency.name,
    }));

    const handleScopeChange = scopeValue => {
        setSavedPage(prevState => ({
            ...prevState,
            scope: scopeValue,
            agencyId: agencies.find(a => a.name === scopeValue)?.id || null,
        }));
        setTemplateScope(scopeValue);
    };

    const handleAvailableTierChange = (tierId, checked) => {
        setSavedPage(prevState => {
            const current = prevState.availableTierIds || [];
            const updated = checked
                ? [...current, tierId]
                : current.filter(id => id !== tierId);
            return { ...prevState, availableTierIds: updated };
        });
    };

    const displayedDataFilter = isAdminAudienceEnabled
        ? savedPage?.dataFilter || userDefaultGroup?.name
        : '';

    return (
        <>
            <div
                style={{
                    width: '576px',
                    minWidth: '300px',
                    margin: 'auto',
                    paddingTop: '45px',
                }}
            >
                <div className="file-form-label">
                    {getText('msg_709a23220f2c')}
                    <span
                        style={{
                            color: '#EF4E45',
                            marginLeft: '4px',
                            fontWeight: '500',
                        }}
                    >
                        *
                    </span>
                    <Tooltip
                        html="Name must be unique across all portal pages."
                        placement="right"
                    >
                        <i
                            className="bi bi-info-circle"
                            style={{
                                paddingLeft: '5px',
                                fontSize: '13px',
                                color: 'var(--iNGAGED-Primary-Color)',
                            }}
                        />
                    </Tooltip>
                </div>
                <input
                    onChange={handleNameChange}
                    defaultValue={savedPage.name}
                    style={{
                        height: '48px',
                        borderRadius: '12px',
                        border: 'solid 1px rgba(47, 43, 67, 0.2)',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                    }}
                    className="form-control"
                    type="text"
                    placeholder={getText('msg_06c0a9b1f504')}
                    aria-label={getText('msg_eac1e09b41f9')}
                ></input>
                <CharacterLimitIndicator
                    maxChars={50}
                    inputStr={savedPage.name || ''}
                />
            </div>

            <div
                style={{
                    width: '576px',
                    minWidth: '300px',
                    margin: 'auto',
                    paddingTop: '20px',
                }}
            >
                <div className="file-form-label">
                    {getText('msg_8d6b34815ba4')}
                    <span
                        style={{
                            color: '#EF4E45',
                            marginLeft: '4px',
                            fontWeight: '500',
                        }}
                    >
                        *
                    </span>
                    <Tooltip
                        html="This will be the name that the individual sees."
                        placement="right"
                    >
                        <i
                            className="bi bi-info-circle"
                            style={{
                                paddingLeft: '5px',
                                fontSize: '13px',
                                color: 'var(--iNGAGED-Primary-Color)',
                            }}
                        />
                    </Tooltip>
                </div>
                <input
                    onChange={handleDisplayNameChange}
                    defaultValue={savedPage.displayName || savedPage.name || ''}
                    style={{
                        height: '48px',
                        borderRadius: '12px',
                        border: 'solid 1px rgba(47, 43, 67, 0.2)',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                    }}
                    className="form-control"
                    type="text"
                    placeholder={getText('msg_d097089fd6e9')}
                    aria-label={getText('msg_40b6f21f2d98')}
                ></input>
                <CharacterLimitIndicator
                    maxChars={50}
                    inputStr={savedPage.displayName || ''}
                />
            </div>

            <div
                style={{
                    width: '576px',
                    minWidth: '300px',
                    margin: 'auto',
                    paddingTop: '45px',
                }}
            >
                <div className="file-form-label">{getText('msg_55f8ebc805e6')}</div>
                <textarea
                    onChange={handleDescriptionChange}
                    defaultValue={savedPage.description}
                    className="form-control"
                    placeholder={getText('msg_1e915442f3fe')}
                    id="floatingTextarea2"
                    style={{
                        height: '100px',
                        borderRadius: '12px',
                        border: 'solid 1px rgba(47, 43, 67, 0.2)',
                        resize: 'none',
                        padding: '16px',
                    }}
                ></textarea>
                <CharacterLimitIndicator
                    maxChars={200}
                    inputStr={savedPage.description || ''}
                />
            </div>

            {!loadFromMultiTenantTemplates && !loadFromClientTemplates && (
                <div
                    style={{
                        width: '576px',
                        minWidth: '300px',
                        margin: 'auto',
                        paddingTop: '45px',
                    }}
                >
                    <div style={{ paddingBottom: '24px' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '8px',
                                paddingBottom: '8px',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    lineHeight: '24px',
                                    color: 'rgba(47, 43, 67, 1)',
                                }}
                            >
                                {getText('msg_e744dd6b3c48')}{' '}
                                <i
                                    className="bi bi-info-circle"
                                    style={{
                                        color: '#1366FF',
                                        marginLeft: '6px',
                                    }}
                                />
                            </div>
                            {isAdminAudienceEnabled &&
                                Boolean(displayedDataFilter) && (
                                    <AudienceBadge
                                        label={displayedDataFilter}
                                    />
                                )}
                        </div>
                        <SearchableDropdown
                            options={populationGroups}
                            optionIDField="id"
                            updateOptions={updatePopulationGroups}
                            savedOptions={savedPage.populationGroups}
                        />
                    </div>
                </div>
            )}
            {loadFromMultiTenantTemplates && (
                <div
                    style={{
                        width: '576px',
                        minWidth: '300px',
                        margin: 'auto',
                        paddingTop: '45px',
                    }}
                >
                    <div className="file-form-label">
                        {getText('msg_4651a34e4df9')}
                        <span
                            style={{
                                color: '#EF4E45',
                                marginLeft: '4px',
                                fontWeight: '500',
                            }}
                        >
                            *
                        </span>
                    </div>
                    <Dropdown
                        defaultValue={templateScope}
                        options={scopeOptions}
                        onChange={value => handleScopeChange(value)}
                        disabled={false}
                    />
                    <div style={{ marginTop: '24px' }}>
                        <div
                            className="checkbox-wrapper"
                            style={{ marginTop: '0px !important' }}
                        >
                            <input
                                type="checkbox"
                                className="form-check-input custom-checkbox"
                                defaultChecked={savedPage.availableTierIds?.includes(
                                    TenantTiers.Bronze,
                                )}
                                onChange={e =>
                                    handleAvailableTierChange(
                                        TenantTiers.Bronze,
                                        e.target.checked,
                                    )
                                }
                            />
                            <span
                                style={{
                                    color: '#2F2B43',
                                    fontWeight: '500',
                                    fontSize: '16px',
                                }}
                            >
                                {getText('msg_076c91fdc798')}
                            </span>
                        </div>
                        <div
                            className="checkbox-wrapper"
                            style={{ marginTop: '0px !important' }}
                        >
                            <input
                                type="checkbox"
                                className="form-check-input custom-checkbox"
                                defaultChecked={savedPage.availableTierIds?.includes(
                                    TenantTiers.Base,
                                )}
                                onChange={e =>
                                    handleAvailableTierChange(
                                        TenantTiers.Base,
                                        e.target.checked,
                                    )
                                }
                            />
                            <span
                                style={{
                                    color: '#2F2B43',
                                    fontWeight: '500',
                                    fontSize: '16px',
                                }}
                            >
                                {getText('msg_a1b5a4255f8c')}
                            </span>
                        </div>
                    </div>
                </div>
            )}
            <div
                style={{
                    width: '576px',
                    minWidth: '300px',
                    margin: 'auto',
                    paddingTop: '30px',
                }}
            >
                {!loadFromMultiTenantTemplates && !loadFromClientTemplates && (
                    <>
                        <div
                            className="checkbox-wrapper"
                            style={{ marginTop: '0px !important' }}
                        >
                            <input
                                type="checkbox"
                                className="form-check-input custom-checkbox"
                                defaultChecked={
                                    savedPage.isChild !== undefined
                                        ? !savedPage.isChild
                                        : null
                                } //We negate the "isChild" property's value to accommodate migration from marking portals as "Child" to "Main Portal", which is the logical opposite
                                onChange={handleIsMainPortal}
                            />
                            <span
                                style={{
                                    color: '#2F2B43',
                                    fontWeight: '500',
                                    fontSize: '16px',
                                }}
                            >
                                {getText('msg_eb0ea3dcaee1')}
                            </span>
                        </div>
                        {clientTierMeetsRequirement(TenantTiers.Silver) && (
                            <div
                                className="checkbox-wrapper"
                                style={{ marginTop: '0px !important' }}
                            >
                                <input
                                    type="checkbox"
                                    className="form-check-input custom-checkbox"
                                    defaultChecked={
                                        savedPage.isAdditionalUserVisible
                                    }
                                    onChange={
                                        handleIsAdditionalUserVisibleChange
                                    }
                                    disabled={
                                        !companyConfig?.application
                                            ?.allow_additional_user
                                    }
                                    style={{
                                        opacity: !companyConfig?.application
                                            ?.allow_additional_user
                                            ? '0.4'
                                            : '1',
                                    }}
                                />
                                <span
                                    style={{
                                        color: !companyConfig?.application
                                            ?.allow_additional_user
                                            ? '#AAAAAA'
                                            : '#2F2B43',
                                        fontWeight: '500',
                                        fontSize: '16px',
                                    }}
                                >
                                    {getText('msg_23c8399f43c0')}
                                </span>

                                {!companyConfig?.application
                                    ?.allow_additional_user && (
                                    <Tooltip
                                        html="Additional user accounts are not enabled."
                                        placement="right"
                                    >
                                        <i
                                            className="bi bi-info-circle"
                                            style={{
                                                paddingLeft: '5px',
                                                fontSize: '13px',
                                                color: 'var(--iNGAGED-Primary-Color)',
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        )}
                    </>
                )}
                {savedPage.id && (
                    <div
                        style={{
                            width: '576px',
                            minWidth: '300px',
                            margin: 'auto',
                            paddingTop: '20px',
                            display: 'flex',
                            gap: '24px',
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <div
                                className="file-form-label"
                                style={{ color: 'rgb(102, 112, 133)' }}
                            >
                                {getText('msg_0c79adcf41ec')}
                            </div>
                            <p>
                                {savedPage.updatedAt
                                    ? getDateFromZulu(savedPage.updatedAt)
                                    : savedPage.createdAt
                                      ? getDateFromZulu(savedPage.createdAt)
                                      : '-'}
                            </p>
                        </div>
                        <div style={{ flex: 1 }}>
                            <div
                                className="file-form-label"
                                style={{ color: 'rgb(102, 112, 133)' }}
                            >
                                {getText('msg_6180d5db3ed9')}
                            </div>
                            <p>
                                {savedPage.updatedBy ||
                                    savedPage.createdBy ||
                                    '-'}
                            </p>
                        </div>
                        {savedPage.draftOfPortalId && (
                            <div style={{ flex: 1 }}>
                                <div
                                    className="file-form-label"
                                    style={{ color: 'rgb(102, 112, 133)' }}
                                >
                                    {getText('msg_4bb53e470484')}
                                </div>
                                <p>
                                    {savedPage.publishDate
                                        ? 'Draft Scheduled'
                                        : 'Draft Saved'}
                                </p>
                            </div>
                        )}
                    </div>
                )}
                <div style={{ marginTop: '60px' }}>
                    <span
                        style={{
                            color: '#EF4E45',
                            marginRight: '4px',
                            fontSize: '14px',
                            fontWeight: '500',
                        }}
                    >
                        *
                    </span>
                    <span
                        style={{
                            color: '#000000',
                            fontSize: '14px',
                            fontWeight: '400',
                        }}
                    >
                        {getText('msg_04148d61b0b7')}
                    </span>
                </div>
                <div
                    onClick={saveCurrentFields}
                    type="button"
                    style={{
                        height: '40px',
                        width: '128px',
                        textAlign: 'center',
                        marginTop: '20px',
                        marginBottom: '60px',
                    }}
                    className="float-end button-base ing-button-primary-left"
                >
                    {getText('msg_bc981983e7f5')}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="8"
                        height="14"
                        viewBox="0 0 8 14"
                        fill="none"
                    >
                        <path
                            d="M1.06494 12.7483L7.10066 7.11968L1.13661 1.41518"
                            stroke="var(--iNGAGED-Neutral-Color)"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>
        </>
    );
};

export default AttributesTab;
